import { useSession } from "@/lib/auth-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState, useRef, useEffect } from "react";
import { Settings, LogOut, User } from "lucide-react";
import { UserAvatar } from "@/components/UserAvatar";
import { useLogout } from "@/hooks/useLogout";

const ROLE_BADGE_STYLES: Record<string, string> = {
  STUDENT: "bg-[#27335D] text-white",
  TEACHER: "bg-[#E8B54B] text-[#27335D]",
  ADMIN: "bg-[#B53135] text-white",
};

export default function UserActions({
  vertical = false,
  setMobileMenuOpen,
}: {
  vertical?: boolean;
  setMobileMenuOpen?: (state: boolean) => void;
}) {
  const { data: session, status } = useSession();
  const pathname = usePathname();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { handleLogout, isLoggingOut, isImpersonating } = useLogout();

  const dashboardLink = "/dashboard";

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (status === "loading") return null;

  const containerClass = vertical
    ? "flex flex-col gap-4"
    : "flex gap-4 items-center";

  if (session) {
    const userName = session.user?.name || "User";
    const initials = userName
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

    // Mobile: inline list items
    if (vertical) {
      return (
        <div className={containerClass}>
          <Link
            href={dashboardLink}
            className="rounded-lg border-2 border-white px-4 py-2 font-medium text-white transition-all hover:bg-white hover:text-[#27335d]"
            onClick={() => setMobileMenuOpen?.(false)}
            data-testid="userActionsDashboardLink"
          >
            Dashboard
          </Link>
          <button
            onClick={() => {
              setMobileMenuOpen?.(false);
              handleLogout();
            }}
            className="cursor-pointer rounded-lg border-2 border-white px-4 py-2 text-left font-medium text-white transition-all hover:bg-white hover:text-[#27335d]"
            data-testid="userActionsLogoutButton"
          >
            {isImpersonating ? "Back to Admin" : "Log Out"}
          </button>
        </div>
      );
    }

    const userRole = (session.user as any)?.role as string | undefined;

    // Desktop: pill dropdown — identical to dashboard user menu
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setDropdownOpen((prev) => !prev)}
          className="flex cursor-pointer items-center gap-3 rounded-full border border-white/20 bg-white/5 py-2 pl-2 pr-5 text-white transition-colors hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40"
          data-testid="userActionsDropdownButton"
        >
          <UserAvatar
            name={userName}
            src={session.user?.image}
            className="h-10 w-10 text-base"
          />
          <span className="flex flex-col items-start leading-tight">
            <span className="max-w-[10rem] truncate text-base font-medium">
              {userName}
            </span>
            {userRole ? (
              <span
                className={`mt-0.5 inline-block rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                  ROLE_BADGE_STYLES[userRole] ?? "bg-white/20 text-white"
                }`}
              >
                {userRole}
              </span>
            ) : null}
          </span>
          <svg
            className={`ml-1 h-4 w-4 transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {dropdownOpen && (
          <div className="absolute right-0 z-50 mt-2 w-52 overflow-hidden rounded-xl border border-white/10 bg-[#1c2746] shadow-xl">
            <div className="border-b border-white/10 px-4 py-3">
              <p className="truncate text-sm font-medium text-white">
                {userName}
              </p>
              <p className="truncate text-xs text-white/50">
                {session.user?.email}
              </p>
              {(session as any)?.impersonatedBy && (
                <p className="mt-1 text-xs font-medium text-amber-400">
                  Impersonating
                </p>
              )}
            </div>
            <div className="py-1">
              <Link
                href={dashboardLink}
                onClick={() => setDropdownOpen(false)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                data-testid="userActionsDashboardLink"
              >
                <User className="h-4 w-4" />
                Dashboard
              </Link>
              <Link
                href="/dashboard/settings"
                onClick={() => setDropdownOpen(false)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              >
                <Settings className="h-4 w-4" />
                Settings
              </Link>
            </div>
            <div className="border-t border-white/10 py-1">
              <button
                onClick={() => {
                  setDropdownOpen(false);
                  handleLogout();
                }}
                disabled={isLoggingOut}
                className="flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-left text-sm text-white/80 transition-colors hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="userActionsLogoutButton"
              >
                <LogOut className="h-4 w-4" />
                {isLoggingOut ? "Logging out..." : isImpersonating ? "Back to Admin" : "Log out"}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Signed-out actions. Sign up is the primary CTA (filled gold), Log in is
  // secondary (subtle outline). The vertical variant (mobile slide-out)
  // uses full-width pills with the same hierarchy.
  const isAuthRoute = pathname === "/auth" || pathname?.startsWith("/auth/");

  if (vertical) {
    return (
      <div className={containerClass}>
        <Link
          href="/auth"
          onClick={() => setMobileMenuOpen?.(false)}
          data-testid="userActionsSignupLink"
          className={`rounded-full px-5 py-3 text-center text-base font-semibold shadow-sm transition-all ${
            isAuthRoute
              ? "bg-[#F6C65A]/90 text-[#27335d]"
              : "bg-[#F6C65A] text-[#27335d] hover:bg-[#e8b54b]"
          }`}
        >
          Sign in or create account
        </Link>
      </div>
    );
  }

  // Desktop/horizontal — same hierarchy.
  return (
    <div className={containerClass}>
      <Link
        href="/auth"
        onClick={() => setMobileMenuOpen?.(false)}
        data-testid="userActionsLoginLink"
        className={`rounded-full px-4 py-2 text-sm font-medium transition-all ${
          isAuthRoute
            ? "text-[#F6C65A]"
            : "text-white hover:bg-white/10"
        }`}
      >
        Sign in
      </Link>
      <Link
        href="/auth?role=student"
        onClick={() => setMobileMenuOpen?.(false)}
        data-testid="userActionsSignupLink"
        className={`rounded-full px-4 py-2 text-sm font-semibold transition-all ${
          isAuthRoute
            ? "bg-[#F6C65A]/90 text-[#27335d]"
            : "bg-[#F6C65A] text-[#27335d] hover:bg-[#e8b54b]"
        }`}
      >
        Create account
      </Link>
    </div>
  );
}
